import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import ErrorBoundary from "@/components/7fcc/ErrorBoundary";
import HomePage from "@/pages/HomePage";
import ReadyPage from "@/pages/ReadyPage";
import ApplyPage from "@/pages/ApplyPage";
import MilitaryPage from "@/pages/MilitaryPage";
import StartPage from "@/pages/StartPage";
import { captureAttribution } from "@/lib/tracking";

const ROUTE_TITLES = {
  "/": "7 Figure Closing Club | Remote Sales Closer Training",
  "/apply": "Apply Now | 7 Figure Closing Club",
  "/ready": "See If You Qualify | 7 Figure Closing Club",
  "/military": "See If You Qualify | 7 Figure Closing Club",
  "/start": "7 Figure Closing Club | Start Here",
};

// Keep the browser tab in sync with the route on client-side navigation.
// Trailing-slash paths (" /ready/") normalize to the same title — the baked
// HTML title would otherwise be overwritten by the homepage fallback — and a
// MutationObserver pins the title so nothing can silently revert it.
function DocumentTitle() {
  const { pathname } = useLocation();
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const title = ROUTE_TITLES[normalized] ?? ROUTE_TITLES["/"];

  useEffect(() => {
    document.title = title;
    const pin = () => {
      if (document.title !== title) document.title = title;
    };
    const observers = [];
    const watch = (el) => {
      const o = new MutationObserver(pin);
      o.observe(el, { childList: true, characterData: true, subtree: true });
      o.__watched = el;
      observers.push(o);
    };
    const titleEl = document.querySelector("title");
    if (titleEl) watch(titleEl);
    // The <title> node itself can be replaced — watch <head> for that.
    const headO = new MutationObserver(() => {
      pin();
      const el = document.querySelector("title");
      if (el && !observers.some((existing) => existing.__watched === el)) watch(el);
    });
    headO.observe(document.head, { childList: true });
    observers.push(headO);
    return () => observers.forEach((o) => o.disconnect());
  }, [title]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    // Preserve utm_*/fbclid/gclid for the future GHL wiring
    captureAttribution();

    // Respect reduced motion; otherwise enable Lenis momentum scrolling
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <DocumentTitle />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/ready" element={<ReadyPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/military" element={<MilitaryPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}